import React from "react";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import { FiArrowUpRight, FiCheck, FiChevronDown, FiEdit2 } from "react-icons/fi";
import StepStatusIcon from "../../atoms/StepStatusIcon/StepStatusIcon";
import {
	getStepItemLabel,
	getStepItemStyle,
} from "../../config/onboardingSteps";
import "./OnboardingStepCard.scss";

const stopToggle = (event) => {
	event.stopPropagation();
};

/**
 * Card desplegable de un paso del onboarding (molecule).
 */
const OnboardingStepCard = ({
	stepKey,
	icon: Icon,
	title,
	description,
	createCtaLabel,
	navCtaLabel,
	createdLabel,
	to,
	items = [],
	secondaryActions = [],
	done = false,
	expanded = true,
	index = 0,
	onToggle,
	onItemClick,
	onCreateClick,
	onCompleteChange,
	markCompleteLabel,
	unmarkCompleteLabel,
}) => {
	const contentId = `onboarding-step-content-${stepKey}`;
	const hasItems = items.length > 0;
	const showHeaderNav = done && !expanded;
	const showChevron = !showHeaderNav;

	const handleKeyDown = (event) => {
		if (event.key === "Enter" || event.key === " ") {
			event.preventDefault();
			onToggle();
		}
	};

	const handleItemClick = (event, item) => {
		stopToggle(event);
		if (typeof onItemClick === "function") {
			onItemClick(item);
		}
	};

	const handleCreateClick = (event) => {
		stopToggle(event);
		if (typeof onCreateClick === "function") {
			onCreateClick();
		}
	};

	const handleCompleteChange = (event) => {
		stopToggle(event);
		if (typeof onCompleteChange === "function") {
			onCompleteChange(event.target.checked);
		}
	};

	const stopCompleteKeys = (event) => {
		if (event.key === " " || event.key === "Enter") {
			event.stopPropagation();
		}
	};

	const handleSecondaryClick = (event, action) => {
		stopToggle(event);
		if (typeof action.onClick === "function") {
			action.onClick();
		}
	};

	const navLink = to && navCtaLabel && (
		<Link to={to} className="onboarding-step__nav" onClick={stopToggle}>
			<span>{navCtaLabel}</span>
			<FiArrowUpRight />
		</Link>
	);

	return (
		<article
			className={[
				"onboarding-step",
				done ? "is-done" : "is-pending",
				expanded ? "is-expanded" : "is-collapsed",
			].join(" ")}
			style={{ "--onboarding-step-index": index }}
			onClick={onToggle}
			onKeyDown={handleKeyDown}
			role="button"
			tabIndex={0}
			aria-expanded={expanded}
			aria-controls={contentId}
		>
			<div className="onboarding-step__summary">
				<div className="onboarding-step__header">
					<div className="onboarding-step__heading">
						{done ? (
							<StepStatusIcon status="done" size="md" />
						) : (
							Icon && (
								<span className="onboarding-step__icon" aria-hidden="true">
									<Icon />
								</span>
							)
						)}
						<div className="onboarding-step__copy">
							<h3 className="onboarding-step__title">{title}</h3>
							{description && (
								<p className="onboarding-step__desc">{description}</p>
							)}
						</div>
					</div>
					{showHeaderNav && navLink}
					{showChevron && (
						<FiChevronDown
							className="onboarding-step__chevron"
							aria-hidden="true"
						/>
					)}
				</div>
			</div>

			<div
				id={contentId}
				className="onboarding-step__details"
				hidden={!expanded}
			>
				{hasItems && (
					<div className="onboarding-step__created">
						<span className="onboarding-step__created-label">
							{createdLabel}
						</span>
						<ul className="list-tags onboarding-step__tags">
							{items.map((item, itemIndex) => (
								<li
									key={item.id ?? `${stepKey}-${itemIndex}`}
									className="tag-default onboarding-step__tag"
									style={getStepItemStyle(stepKey, item)}
									onClick={(event) => handleItemClick(event, item)}
									onKeyDown={(event) => {
										if (event.key === "Enter" || event.key === " ") {
											event.preventDefault();
											handleItemClick(event, item);
										}
									}}
									role="button"
									tabIndex={0}
									aria-label={getStepItemLabel(stepKey, item)}
								>
									<span className="onboarding-step__tag-label">
										{getStepItemLabel(stepKey, item)}
									</span>
									<span
										className="onboarding-step__tag-edit"
										aria-hidden="true"
									>
										<FiEdit2 />
									</span>
								</li>
							))}
						</ul>
					</div>
				)}

				<div className="onboarding-step__actions">
					{secondaryActions.map((action) => (
						<button
							key={action.key}
							type="button"
							className="btn onboarding-step__secondary-btn"
							onClick={(event) => handleSecondaryClick(event, action)}
						>
							{action.label}
						</button>
					))}

					{createCtaLabel && (
						<button
							type="button"
							className="btn btn-primary onboarding-step__create"
							onClick={handleCreateClick}
						>
							{createCtaLabel}
						</button>
					)}

					{!showHeaderNav && navLink}
				</div>

				<div
					className="onboarding-step__complete"
					onClick={stopToggle}
					onKeyDown={stopCompleteKeys}
				>
					<label className="onboarding-step__mark">
						<input
							type="checkbox"
							className="onboarding-step__mark-input"
							checked={done}
							onChange={handleCompleteChange}
							onClick={stopToggle}
						/>
						<span className="onboarding-step__mark-box" aria-hidden="true">
							<FiCheck />
						</span>
						<span className="onboarding-step__mark-text">
							{done ? unmarkCompleteLabel : markCompleteLabel}
						</span>
					</label>
				</div>
			</div>
		</article>
	);
};

OnboardingStepCard.propTypes = {
	stepKey: PropTypes.string.isRequired,
	icon: PropTypes.elementType,
	title: PropTypes.string,
	description: PropTypes.string,
	createCtaLabel: PropTypes.string,
	navCtaLabel: PropTypes.string,
	createdLabel: PropTypes.string,
	to: PropTypes.string,
	items: PropTypes.arrayOf(PropTypes.object),
	secondaryActions: PropTypes.arrayOf(
		PropTypes.shape({
			key: PropTypes.string.isRequired,
			label: PropTypes.string.isRequired,
			onClick: PropTypes.func.isRequired,
		})
	),
	done: PropTypes.bool,
	expanded: PropTypes.bool,
	index: PropTypes.number,
	onToggle: PropTypes.func,
	onItemClick: PropTypes.func,
	onCreateClick: PropTypes.func,
	onCompleteChange: PropTypes.func,
	markCompleteLabel: PropTypes.string,
	unmarkCompleteLabel: PropTypes.string,
};

export default OnboardingStepCard;
